import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { FileText, Loader2, Plus, Trash2 } from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiFetch } from '../../fetchInterceptor';
import { FieldPlacementEditor } from './FieldPlacementEditor';
import { PdfDropZone } from './PdfDropZone';
import { fileToBase64 } from './helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tab inside SignaturesHub that lists existing reusable templates and lets
 * the user create new ones (upload a PDF, place fields, save). Each saved
 * template can be re-used by the New Request Wizard to skip re-placing
 * fields for the same engagement letter / contract every time.
 *
 * Extracted from pages/SignaturesHub.jsx.
 */
export function TemplatesTab({ templates, loading, onRefresh }) {
  const { currentWorkspace } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [pdfDataUri, setPdfDataUri] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Please upload a PDF'); return; }
    const dataUri = await fileToBase64(file);
    setPdfDataUri(dataUri);
    setPdfName(file.name);
    setFields([]);
  };

  const handleSave = async () => {
    if (!newName.trim()) { toast.error('Name the template'); return; }
    if (!pdfDataUri) { toast.error('Upload a PDF'); return; }
    if (!fields.length) { toast.error('Place at least one field'); return; }
    setSaving(true);
    try {
      const res = await apiFetch(
        `${API_URL}/api/esign/templates?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName.trim(), pdf_base64: pdfDataUri, fields }),
        },
      );
      if (!res.ok) throw new Error((await res.json()).detail?.message || 'Failed to save template');
      toast.success(`Template "${newName}" saved`);
      setCreating(false);
      setNewName('');
      setPdfDataUri(null);
      setPdfName('');
      setFields([]);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tid) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const res = await apiFetch(
        `${API_URL}/api/esign/templates/${tid}?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Template deleted');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (creating) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">New template</CardTitle>
          <CardDescription>Upload a PDF once and reuse it for every client. Field positions are saved.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Template name</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Engagement Letter 2026" data-testid="template-name-input" />
          </div>
          <div className="space-y-1">
            <Label>PDF</Label>
            <PdfDropZone onFile={handleFile} fileName={pdfName} testId="template-pdf-input" />
          </div>
          {pdfDataUri && (
            <FieldPlacementEditor pdfDataUri={pdfDataUri} fields={fields} onChange={setFields} />
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setCreating(false); setNewName(''); setPdfDataUri(null); setPdfName(''); setFields([]); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save template
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} className="gap-2" data-testid="new-template-btn">
          <Plus className="w-4 h-4" /> New template
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : templates.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No templates yet. Create one to skip re-placing fields every time.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {templates.map(t => (
            <Card key={t.template_id} data-testid={`template-row-${t.template_id}`}>
              <CardContent className="py-3 flex items-center gap-3">
                <FileText className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{(t.fields?.length || 0)} field{t.fields?.length === 1 ? '' : 's'}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(t.template_id)} aria-label="Delete template">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default TemplatesTab;
